import { useQuery } from "@apollo/client";
import { InnerSidebar, InnerSidebarItem } from "@aurem/components";
import { Avatar } from "@aurem/ui";
import { ProductAssignmentSelector } from "components/ProductAssignmentSelector";
import { useActiveProductAssignment } from "hooks/employee";
import { useProductEosb } from "hooks/employee/useEosbProduct";
import { useNavigation } from "hooks/navigate";
import { useBasePath } from "hooks/useBasePath";
import {
  ArrowDownToLineIcon,
  FileTextIcon,
  HouseIcon,
  LayersIcon,
  PlusIcon,
  ReceiptIcon,
} from "lucide-react";
import { useEmployment } from "providers/employee/EmploymentProvider";
import { useI18n } from "providers/I18nProvider";
import { FC, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { gql } from "types";

const GET_USER_COMPANIES_AND_PRODUCTS = gql(/* GraphQL */ `
  query UserCompaniesAndProducts {
    user {
      id
      employments {
        id
        productAssignments {
          id
          actions {
            setFund {
              isEnabled
            }
            setSalaryContribution {
              isEnabled
            }
            withdraw {
              isEnabled
            }
          }
          productInstance {
            id
            name
          }
        }
        company {
          id
          name
        }
      }
    }
  }
`);

const EXCLUDED_ROUTES = [
  "/onboarding",
  "/disclaimers",
  "/overview",
  "/settings",
] as const;

export const LayoutInnerSidebar = () => {
  const { pathname } = useLocation();
  const shouldHide = EXCLUDED_ROUTES.some((route) => pathname.includes(route));

  if (shouldHide) {
    return null;
  }

  return <LayoutInnerSidebarView />;
};

const LayoutInnerSidebarView = () => {
  const { language } = useI18n();
  const navigate = useNavigation();
  const { employmentId: urlEmploymentId, productAssignmentId } = useParams();
  const {
    employmentId,
    setEmploymentId,
    isTerminated,
    employment,
    loading: employmentLoading,
  } = useEmployment();
  const { isActive: isEosbActive } = useProductEosb();
  const { currentAssignment } = useActiveProductAssignment();
  const activeProductName = currentAssignment?.productInstance?.name;
  const { data, loading } = useQuery(GET_USER_COMPANIES_AND_PRODUCTS);
  const productAssignments = employment?.productAssignments ?? [];
  const employments = data?.user.employments ?? [];
  const firstEmploymentId = employments[0]?.id;
  const validCompanyIds = employments.map((c) => c.id);

  const isUrlCompanyIdValid =
    !urlEmploymentId || validCompanyIds.includes(urlEmploymentId);

  useEffect(() => {
    if (!loading && urlEmploymentId && !isUrlCompanyIdValid) {
      navigate(`/${language}/not-found/`, { replace: true });
    }
  }, [loading, urlEmploymentId, isUrlCompanyIdValid, language, navigate]);

  useEffect(() => {
    if (!employmentId && firstEmploymentId && isUrlCompanyIdValid) {
      setEmploymentId(firstEmploymentId);
    }
  }, [employmentId, firstEmploymentId, setEmploymentId, isUrlCompanyIdValid]);

  const selected =
    employments?.find((e) => e.id === employmentId) ?? employments?.[0];

  // Hide inner sidebar if the employee has no products assigned
  if (!employmentLoading && productAssignments.length === 0) {
    return null;
  }

  return (
    <InnerSidebar
      header={
        <ProductAssignmentSelector className="h-16 justify-between shrink-0 px-3 py-1.5 outline-none" />
      }
      collapsedLogo={
        <Avatar text={activeProductName ?? ""} size="md" shape="round" />
      }
    >
      <SidebarContent
        employments={employments}
        selectedEmploymentId={selected?.id || ""}
        isTerminated={isTerminated}
        isEosbActive={isEosbActive}
        actions={
          (productAssignmentId
            ? selected?.productAssignments?.find(
                (assignment) => assignment.id === productAssignmentId,
              )
            : selected?.productAssignments?.[0]
          )?.actions
        }
      />
    </InnerSidebar>
  );
};

type SidebarContentProps = {
  employments: Array<{
    id: string;
    company: {
      id: string;
      name: string;
    };
  }>;
  selectedEmploymentId: string;
  isTerminated: boolean;
  isEosbActive: boolean;
  actions?: {
    setFund: {
      isEnabled: boolean;
    };
    setSalaryContribution: {
      isEnabled: boolean;
    };
    withdraw: {
      isEnabled: boolean;
    };
  };
};

export const SidebarContent: FC<SidebarContentProps> = ({
  employments = [],
  selectedEmploymentId,
  isTerminated,
  isEosbActive,
  actions,
}) => {
  const { t } = useI18n();
  const { path } = useBasePath();
  const { productAssignmentId: urlProductAssignmentId } = useParams();
  const { employment } = useEmployment();
  const { isCurrentActive } = useActiveProductAssignment();

  const selectedEmployment =
    employments.find((e) => e.id === selectedEmploymentId) ?? employments[0];

  const productAssignments = employment?.productAssignments ?? [];
  const firstAssignmentId = productAssignments[0]?.id;
  const productAssignmentId = urlProductAssignmentId ?? firstAssignmentId;

  const buildEmploymentPath = (subPath: string) => {
    const employmentId = selectedEmployment?.id;
    return path(
      `/employment/${employmentId}/${productAssignmentId}/${subPath}`,
    );
  };

  function renderMainPanel() {
    const items = [
      {
        subPath: "dashboard",
        label: t("apps.employee.navigation.dashboard"),
        icon: <HouseIcon size={16} />,
        show: true,
      },
      {
        subPath: "contributions-setup",
        label: t("apps.employee.navigation.contributions"),
        icon: <PlusIcon size={16} />,
        show:
          isEosbActive &&
          isCurrentActive &&
          !isTerminated &&
          actions?.setSalaryContribution?.isEnabled,
      },
      {
        subPath: "sub-funds",
        label: t("apps.employee.navigation.funds"),
        icon: <LayersIcon size={16} />,
        show: actions?.setFund?.isEnabled,
      },
      {
        subPath: "transactions",
        label: t("apps.employee.navigation.transactions"),
        icon: <ReceiptIcon size={16} />,
        show: true,
      },
      {
        subPath: "withdrawals",
        label: t("apps.employee.pages.withdrawals.page.title"),
        icon: <ArrowDownToLineIcon size={16} />,
        show: actions?.withdraw?.isEnabled,
      },
      {
        subPath: "statements",
        label: t("apps.employee.navigation.statements"),
        icon: <FileTextIcon size={16} />,
        show: true,
      },
    ];

    return (
      <>
        {items
          .filter((i) => i.show)
          .map((item) => (
            <InnerSidebarItem
              key={item.subPath}
              to={buildEmploymentPath(item.subPath)}
              icon={item.icon}
              label={item.label}
            />
          ))}
      </>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto py-2 space-y-2 text-sm">
        {renderMainPanel()}
      </div>
    </div>
  );
};
